/**
 * Text and case transforms used across the SPA.
 *
 * Date/number formatters live in `lib/format.ts`, URL helpers in `lib/url.ts`.
 * Inline markdown rendering lives in `lib/markdown.tsx`.
 */

/**
 * Convert snake_case state strings (e.g. "bid_requested") to display labels
 * ("Bid Requested"). Keep in sync with the `humanize_state` helper in
 * `dashboard.rs` — both sides render the same labels.
 */
export function humanizeState(state: string): string {
  return state
    .split("_")
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Convert enum-style codes ("AUTOMOTIVE", "food-service", "retail_trade")
 * into title-cased display strings ("Automotive", "Food Service",
 * "Retail Trade"). Splits on whitespace, hyphen, and underscore.
 *
 * Distinct from `humanizeState` — that one only splits on underscore and
 * is paired 1:1 with a Rust equivalent. `titleCase` is broader.
 */
export function titleCase(s: string): string {
  return s
    .toLowerCase()
    .split(/[\s_-]+/)
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/**
 * Capitalize the first letter and leave the rest alone: "daily" → "Daily",
 * "assessed Sep 23" → "Assessed Sep 23". Unlike `titleCase`, which lower-cases
 * every word first, codes and names inside the string ("PJM", "Sep") survive.
 */
export function capitalizeFirst(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}
